import React, { useMemo, useState, useCallback } from 'react';
import { Eye, Loader2, RefreshCw, Download } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { CollapsibleFilterBar } from '../materials/tabs/Filters/CollapsibleFilterBar';
import { DateRangePicker } from '../../../../../components/ui/date-range-picker';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { Pagination } from '../../../../../components/ui/Pagination';
import { cn } from '../../../../../lib/utils';
import { useAuditTrailQuery } from './useAuditTrailQuery';
import { AuditTrailDetailSheet } from './AuditTrailDetailSheet';
import { UserActivityTimelineView } from './UserActivityTimelineView';
import { EntityHistoryView } from './EntityHistoryView';
import { AuditProjectFilter } from './AuditProjectFilter';
import type { AuditTimelineFilters } from './useAuditTrailTimelineQuery';
import { downloadAuditTrailCsv } from '../../../../audit/auditTrailExport';
import { logActivityExport, logActivityPageView } from '../../../../audit/activityAudit';
import type { AuditTrailItem } from './auditTrailTypes';
import {
  AUDIT_MODULES,
  SEVERITY_OPTIONS,
  formatAuditTrailDateTime,
  getActorDisplay,
  getModuleLabel,
  getEventDisplayLabel,
  getActivityEntityDisplayName,
  getEventsForModule,
  getProjectSummaryDisplay,
  getAuditSummary,
} from './auditTrailConstants';
import {
  AUDIT_TAB_LIST_CLASS,
  AUDIT_TAB_TRIGGER_CLASS,
  SeverityBadge,
  EntityTypeBadge,
  AuditEmptyState,
  AuditErrorBanner,
  AuditLoadingState,
} from './auditTrailUi';

type AuditViewMode = 'log' | 'timeline' | 'entity';

interface AuditTrailViewProps {
  projectId?: number;
  projectName?: string | null;
  enableProjectFilter?: boolean;
  showProjectColumn?: boolean;
  description?: string;
}

const VIEW_TAB_LABELS: Record<AuditViewMode, string> = {
  log: 'Event log',
  timeline: 'User activity',
  entity: 'Entity history',
};

export const AuditTrailView: React.FC<AuditTrailViewProps> = ({
  projectId: fixedProjectId,
  projectName: fixedProjectName,
  enableProjectFilter = false,
  showProjectColumn = false,
  description = 'Project activity log for compliance and troubleshooting. Filter by module, event, actor, or date range.',
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState('__all__');

  const resolvedProjectId = enableProjectFilter
    ? selectedProjectId === '__all__'
      ? undefined
      : Number(selectedProjectId)
    : fixedProjectId;

  const resolvedProjectName = enableProjectFilter ? undefined : fixedProjectName?.trim() || undefined;

  const showProjectInTable = enableProjectFilter
    ? selectedProjectId === '__all__'
    : showProjectColumn;

  const {
    filters,
    updateFilters,
    resetFilters,
    page,
    setPage,
    itemsPerPage,
    setItemsPerPage,
    items,
    total,
    loading,
    error,
    unavailable,
    reload,
  } = useAuditTrailQuery(resolvedProjectId, resolvedProjectName);

  const [selectedItem, setSelectedItem] = useState<AuditTrailItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewMode, setViewMode] = useState<AuditViewMode>('log');
  const [quickFilter, setQuickFilter] = useState<'none' | 'today' | 'failures'>('none');
  const [timelineFilters, setTimelineFilters] = useState<AuditTimelineFilters>(() => ({
    actorId: '',
    dateFrom: undefined,
    dateTo: undefined,
  }));

  const updateTimelineFilters = useCallback((patch: Partial<AuditTimelineFilters>) => {
    setTimelineFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const eventOptions = useMemo(
    () => getEventsForModule(filters.module || undefined),
    [filters.module]
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.module) n += 1;
    if (filters.eventType) n += 1;
    if (filters.severities.length > 0) n += 1;
    if (filters.actorId.trim()) n += 1;
    if (filters.search.trim()) n += 1;
    if (filters.dateFrom || filters.dateTo) n += 1;
    if (enableProjectFilter && selectedProjectId !== '__all__') n += 1;
    return n;
  }, [filters, enableProjectFilter, selectedProjectId]);

  const handleClearFilters = useCallback(() => {
    setQuickFilter('none');
    if (enableProjectFilter) {
      setSelectedProjectId('__all__');
    }
    resetFilters();
  }, [resetFilters, enableProjectFilter]);

  const applyQuickFilter = useCallback(
    (mode: 'today' | 'failures') => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const end = new Date(today);
      end.setHours(23, 59, 59, 999);

      if (mode === 'today') {
        setQuickFilter('today');
        updateFilters({
          dateFrom: today,
          dateTo: end,
          severities: [],
        });
        return;
      }

      setQuickFilter('failures');
      updateFilters({
        severities: ['ERR'],
      });
    },
    [updateFilters]
  );

  const handleDateRangeUpdate = useCallback(
    (values: {
      dateFrom: Date | undefined;
      dateTo: Date | undefined;
      compareFrom?: Date | undefined;
      compareTo?: Date | undefined;
    }) => {
      updateFilters({
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
      });
    },
    [updateFilters]
  );

  const openDetail = (row: AuditTrailItem) => {
    setSelectedItem(row);
    setDetailOpen(true);
  };

  const handleViewModeChange = (value: string) => {
    const mode = value as AuditViewMode;
    logActivityPageView(`Audit Trail — ${VIEW_TAB_LABELS[mode]}`, {
      tab: mode,
      projectId: resolvedProjectId,
      projectName: resolvedProjectName ?? null,
    });
    setViewMode(mode);
  };

  const handleExportCsv = () => {
    downloadAuditTrailCsv(items);
    logActivityExport('audit-trail-csv', {
      count: items.length,
      projectId: resolvedProjectId,
      projectName: resolvedProjectName ?? null,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-blueprint-blue">Audit Trail</h2>
          <p className="text-sm text-heritage-grey mt-1 max-w-xl">{description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {viewMode === 'log' && items.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 border-[#E2E5EA]"
              onClick={handleExportCsv}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 border-[#E2E5EA]"
            onClick={() => void reload()}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={viewMode} onValueChange={handleViewModeChange} className="w-full">
        <div className="border-b border-[#E2E5EA]">
          <TabsList className={AUDIT_TAB_LIST_CLASS}>
            <TabsTrigger value="log" className={AUDIT_TAB_TRIGGER_CLASS}>
              Event log
            </TabsTrigger>
            <TabsTrigger value="timeline" className={AUDIT_TAB_TRIGGER_CLASS}>
              User activity
            </TabsTrigger>
            <TabsTrigger value="entity" className={AUDIT_TAB_TRIGGER_CLASS}>
              Entity history
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="timeline" className="mt-4 focus-visible:outline-none">
          <UserActivityTimelineView
            projectId={resolvedProjectId}
            filters={timelineFilters}
            updateFilters={updateTimelineFilters}
            onOpenDetail={openDetail}
          />
        </TabsContent>

        <TabsContent value="entity" className="mt-4 focus-visible:outline-none">
          <EntityHistoryView
            projectId={resolvedProjectId}
            projectName={resolvedProjectName}
            onOpenDetail={openDetail}
          />
        </TabsContent>

        <TabsContent value="log" className="mt-4 focus-visible:outline-none">
          {error ? (
            <AuditErrorBanner message={error} onRetry={() => void reload()} unavailable={unavailable} />
          ) : null}

          <CollapsibleFilterBar
            searchQuery={filters.search}
            onSearchChange={(value) => updateFilters({ search: value })}
            searchPlaceholder="Search actor, entity, event, summary..."
            activeFilterCount={activeFilterCount}
            filteredCount={total}
            filteredCountLabel="matching events"
            onResetFilters={handleClearFilters}
          >
            <div className="col-span-full space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 items-end">
                {enableProjectFilter ? (
                  <AuditProjectFilter
                    value={selectedProjectId}
                    onChange={setSelectedProjectId}
                  />
                ) : null}

                <div className="flex flex-col gap-1 min-w-0">
                  <Label className="text-[11px] text-heritage-grey">Module</Label>
                  <Select
                    value={filters.module || '__all__'}
                    onValueChange={(v) =>
                      updateFilters({
                        module: v === '__all__' ? '' : v,
                        eventType: '',
                      })
                    }
                  >
                    <SelectTrigger className="h-9 text-[13px]">
                      <SelectValue placeholder="All modules" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All modules</SelectItem>
                      {AUDIT_MODULES.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1 min-w-0">
                  <Label className="text-[11px] text-heritage-grey">Event type</Label>
                  <Select
                    value={filters.eventType || '__all__'}
                    onValueChange={(v) => updateFilters({ eventType: v === '__all__' ? '' : v })}
                  >
                    <SelectTrigger className="h-9 text-[13px]">
                      <SelectValue placeholder="All events" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All events</SelectItem>
                      {eventOptions.map((e) => (
                        <SelectItem key={e.value} value={e.value}>
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1 min-w-0">
                  <Label className="text-[11px] text-heritage-grey">Severity</Label>
                  <MultiSelect
                    options={SEVERITY_OPTIONS}
                    selected={filters.severities}
                    onChange={(selected) => updateFilters({ severities: selected })}
                    placeholder="Any severity"
                  />
                </div>

                <div className="flex flex-col gap-1 min-w-0">
                  <Label className="text-[11px] text-heritage-grey">Actor</Label>
                  <Input
                    className="h-9 text-[13px]"
                    placeholder="Name, email, or user ID"
                    value={filters.actorId}
                    onChange={(e) => updateFilters({ actorId: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between pt-3 border-t border-[#EEF0F3]">
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-[11px] text-heritage-grey sr-only">Quick filters</Label>
                  <Button
                    type="button"
                    variant={quickFilter === 'today' ? 'default' : 'outline'}
                    size="sm"
                    className={cn(
                      'h-8 text-[12px]',
                      quickFilter === 'today' && 'bg-blueprint-blue hover:bg-blueprint-blue/90'
                    )}
                    onClick={() => applyQuickFilter('today')}
                  >
                    Today
                  </Button>
                  <Button
                    type="button"
                    variant={quickFilter === 'failures' ? 'default' : 'outline'}
                    size="sm"
                    className={cn(
                      'h-8 text-[12px]',
                      quickFilter === 'failures' && 'bg-readable-red hover:bg-readable-red/90'
                    )}
                    onClick={() => applyQuickFilter('failures')}
                  >
                    Failures only
                  </Button>
                </div>

                <DateRangePicker
                  initialDateFrom={filters.dateFrom}
                  initialDateTo={filters.dateTo}
                  onUpdate={handleDateRangeUpdate}
                  showCompare={false}
                />
              </div>
            </div>
          </CollapsibleFilterBar>

          <Card className="border-[#E2E5EA] shadow-none">
            <CardContent className="p-6">
              {loading && items.length === 0 ? (
                <AuditLoadingState message="Loading audit events…" />
              ) : items.length === 0 ? (
                <AuditEmptyState
                  title="No audit events match your filters"
                  description={
                    unavailable
                      ? 'When the audit trail API is enabled, events will appear here.'
                      : 'Try adjusting filters or clearing the search.'
                  }
                />
              ) : (
                <>
                  <div className="overflow-x-auto -mx-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>
                            {showProjectInTable ? 'Project / Summary' : 'Summary'}
                          </TableHead>
                          <TableHead>Severity</TableHead>
                          <TableHead>Module</TableHead>
                          <TableHead>Event</TableHead>
                          <TableHead>Actor</TableHead>
                          <TableHead>Entity</TableHead>
                          <TableHead className="w-[72px] text-right">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((row) => {
                          const actor = getActorDisplay(row);
                          const projectSummary = getProjectSummaryDisplay(row, {
                            showProjectName: showProjectInTable,
                          });
                          return (
                            <TableRow key={row.id}>
                              <TableCell className="whitespace-nowrap">
                                {formatAuditTrailDateTime(row.created_at)}
                              </TableCell>
                              <TableCell className="max-w-[280px]">
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-[12px] font-medium text-text-grey line-clamp-2">
                                    {showProjectInTable
                                      ? projectSummary.primary
                                      : getAuditSummary(row)}
                                  </span>
                                  <span className="text-[11px] text-heritage-grey line-clamp-2">
                                    {showProjectInTable
                                      ? projectSummary.secondary !== projectSummary.primary
                                        ? projectSummary.secondary
                                        : getEventDisplayLabel(row)
                                      : getProjectSummaryDisplay(row, { showProjectName: true }).primary}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <SeverityBadge severity={row.severity} />
                              </TableCell>
                              <TableCell title={row.module}>{getModuleLabel(row.module)}</TableCell>
                              <TableCell title={row.event_type}>{getEventDisplayLabel(row)}</TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="text-[12px] text-text-grey">{actor.primary}</span>
                                  {actor.secondary ? (
                                    <span className="text-[11px] text-heritage-grey">{actor.secondary}</span>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-[12px] text-text-grey">
                                    {getActivityEntityDisplayName(row) ||
                                      row.entity_name ||
                                      row.entity_id ||
                                      '—'}
                                  </span>
                                  {row.entity_type ? <EntityTypeBadge type={row.entity_type} /> : null}
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-[12px] text-blueprint-blue"
                                  onClick={() => openDetail(row)}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {loading ? (
                    <div className="flex justify-center py-2">
                      <Loader2 className="h-5 w-5 animate-spin text-heritage-grey" />
                    </div>
                  ) : null}

                  <div className="mt-4 pt-4 border-t border-[#EEF0F3]">
                    <Pagination
                      currentPage={page}
                      totalItems={total}
                      itemsPerPage={itemsPerPage}
                      onPageChange={setPage}
                      onItemsPerPageChange={(n) => {
                        setItemsPerPage(n);
                        setPage(1);
                      }}
                      pageSizeOptions={[25, 50, 100]}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AuditTrailDetailSheet
        item={selectedItem}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
};
